import EcoDesignEventsBus from 'eco-design-micro/EcoDesignEventsBus';
import { isH5HandleClient } from 'eco-design-micro/utils-base/isH5Handle';
import gtm from 'eco-design-reliance/gtm';
import { useEffect, useRef, useState } from 'react';

// 监听面板高度变化
export const usePanelHeight = () => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    // 动态计算内容高度
    useEffect(() => {
        if (!contentRef.current) return;
        const observer = new ResizeObserver((e) => {
            e.forEach((item) => {
                const isH5 = isH5HandleClient();
                if (isH5) return;
                setHeight(item.contentRect.height);
            });
        });
        observer.observe(contentRef.current);
        return () => observer.disconnect();
    }, []);

    return { contentRef, height };
};

// 导航栏切换逻辑
export const useNavListSwitch = () => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [isHrefItem, setIsHrefItem] = useState(false);

    const tabLineRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [lineStyle, setLineStyle] = useState({ opacity: 0, left: 0, width: 0 });
    // ⭐ 用来控制延迟触发
    const hoverTimer = useRef<NodeJS.Timeout | null>(null);

    const hasDropdown = (child: any) => !!child.props.children;

    const activateNavItem = (index: number, child: any) => {
        if (activeIndex === null) {
            // null 的时候去掉动画
            tabLineRef.current?.classList.remove('use-transition');
        } else {
            tabLineRef.current?.classList.add('use-transition');
        }
        setIsHrefItem(!hasDropdown(child));
        setActiveIndex(index);
        EcoDesignEventsBus.emit('ef-header-search-close');
        const el = itemRefs.current[index];
        if (el) {
            setLineStyle({
                opacity: 1,
                left: el.offsetLeft,
                width: el.offsetWidth,
            });
        }
    };

    const handleClickEnter = (index: number, child: any) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'top_first_navigation',
            navigation_name: child.props.title,

            __btn_id1: `header-nvd-t1-i`,
            __btn_id2: String(child.props.title).toLowerCase().trim(),
            __btn_type: 'common',
        });
        if (!hasDropdown(child)) {
            // 文案链接模式
            if (child.props.href && isH5HandleClient()) {
                window.open(child.props.href);
            }
            return;
        }
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
        activateNavItem(index, child);
    };

    const handleMouseEnter = (index: number, child: any) => {
        if (isH5HandleClient()) return;
        if (!hasDropdown(child)) return;
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
        }
        hoverTimer.current = setTimeout(() => {
            activateNavItem(index, child);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (isH5HandleClient()) return;

        setActiveIndex(null);
        EcoDesignEventsBus.emit('ef-header-search-close');
        setLineStyle((prev) => ({ ...prev, opacity: 0 }));
    };

    const handleMouseLeaveTab = () => {
        if (isH5HandleClient()) return;
        // 离开时取消 hover 延时
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    };

    // 监听移动端关闭
    useEffect(() => {
        EcoDesignEventsBus.on('global-header-nav-item1-close', () => {
            setActiveIndex(null);
        });
    }, []);

    return {
        activeIndex,
        lineStyle,
        handleClickEnter,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseLeaveTab,
        tabLineRef,
        itemRefs,
        isHrefItem,
    };
};

// 监听 serach 面板打开情况
export const useSearchOpen = () => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const callback: MutationCallback = (mutations) => {
            mutations.forEach((mutation) => {
                const el = mutation.target as HTMLDivElement;
                setOpen(mutation.attributeName === 'class' && el.classList.contains('ef-header-v2-search-open'));
            });
        };
        const mutation = new MutationObserver(callback);
        // 属性监听，知道是否打开搜索框
        const el = document.querySelector(`.ef-header-v2-bar-search`);
        if (el) {
            mutation.observe(el, {
                attributes: true,
            });
            return () => mutation.disconnect();
        }
    }, []);
    return { open };
};
